import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { Flex, Icon, IconDictionary, Spacer, Stack, Text } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { AnalyticProvider, useAnalyticContext } from '@components/analytic_link/analytic_link.provider';
import { LinkInMessage } from '@components/link_in_message.component';
import { SuspendedPresentation } from '@components/suspended_presentation.component';
import { withTranslation } from '@modules/i18n_and_l10n/with_translation';
import type { AppConfig } from '@services/config.service';
import { useService } from '@visiba/instantiation';
import { rem } from '@visiba/std';
import { Fragment, MouseEvent, useEffect } from 'react';
import styled from 'styled-components';
import { ConsentTrigger } from '../../../../patient/src/components/consent/consent_trigger.component';
import { ASSET_IMAGE_PATH } from '../../constants';
import { ReadMoreText } from '../read_more_text.component';
import { AddressField, ExternalLink, FooterController, LegalField } from './_footer.controller';
import { FooterSkeleton } from './_footer.skeleton.component';
import { FooterLayout } from './_footer_layout.component';

interface Props {
	__TEMP__changeLanguage?(langCode: string): void;
}

const elements = {
	link: styled.a`
		line-height: ${rem(24)};
		font-size: ${rem(24)};
	`,
	linkIconContainer: styled.div`
		color: ${tokens.color.content.interactive};
		flex-shrink: 0;
	`,
	appLogoImage: styled.img`
		height: ${rem(45)};
	`,
	appLink: styled.a`
		display: block;
	`,
};

export function Footer({ __TEMP__changeLanguage }: Props): JSX.Element {
	const footerController = useService(FooterController);

	useEffect(() => {
		footerController.onMount();
	}, [footerController]);

	return (
		<AnalyticProvider category={AnalyticCategories.Footer}>
			<SuspendedPresentation controller={footerController} fallback={<FooterSkeleton />}>
				{({ aboutUsText, addressInfo, externalLink, legal, appConfig }) => {
					return (
						<FooterLayout
							addressInfoSection={<AddressInfoSection addressInfo={addressInfo} />}
							externalLinkSection={<ExternalLinkSection externalLink={externalLink} />}
							infoTextSection={<InfoTextSection aboutUsText={aboutUsText} />}
							legalSection={<LegalSection legalFields={legal} />}
							appSection={<AppSection appConfig={appConfig} />}
							__TEMP__changeLanguage={__TEMP__changeLanguage}
						/>
					);
				}}
			</SuspendedPresentation>
		</AnalyticProvider>
	);
}

function AddressInfoSection({ addressInfo }: { addressInfo: AddressField[] }): JSX.Element {
	return (
		<Fragment>
			{addressInfo.map((addressField) => (
				<Text key={addressField.key} variant='bodyRegular' color={tokens.color.content.brand}>
					{addressField.value}
				</Text>
			))}
		</Fragment>
	);
}

function ExternalLinkSection({ externalLink }: { externalLink: ExternalLink | null }): JSX.Element | null {
	const analyticService = useService(AnalyticService);
	const analyticContext = useAnalyticContext();

	if (externalLink == null) return null;

	function analyticQuirk_handleClick(): void {
		analyticService.trackEvent(analyticContext, 'Clicked: Website-link');
	}

	return (
		<LinkWithIcon icon='network' link={externalLink.url} onClick={analyticQuirk_handleClick}>
			{externalLink.name ?? externalLink.url}
		</LinkWithIcon>
	);
}

function InfoTextSection({ aboutUsText }: { aboutUsText: string | null }): JSX.Element | null {
	if (aboutUsText == null) return null;

	return (
		<ReadMoreText open={false} data-testid='ReadMoreText' maxLineNumber={3} title={withTranslation('PW.Component.Footer.Text.Title')}>
			<LinkInMessage text={aboutUsText} />
		</ReadMoreText>
	);
}

function getLegalFieldLabel(legalField: LegalField): string {
	if (legalField.key === 'tos') {
		return 'Terms of service';
	}
	if (legalField.key === 'pp') {
		return 'Privacy policy';
	}
	return legalField.key;
}

function LegalSection({ legalFields }: { legalFields: LegalField[] }): JSX.Element {
	const analyticService = useService(AnalyticService);
	const analyticContext = useAnalyticContext();

	function analyticQuirk_handleClick(legalField: LegalField): () => void {
		return () => {
			analyticService.trackEvent(analyticContext, `Clicked: "${getLegalFieldLabel(legalField)}" link`);
		};
	}

	// NHS specification requires legal links to open in a new tab.
	const legalSection = legalFields.map((legalField) => (
		<LinkWithIcon
			target={legalField.target ? legalField.target : '_blank'}
			key={legalField.key}
			icon='linkOut'
			link={legalField.value}
			onClick={analyticQuirk_handleClick(legalField)}
		>
			{withTranslation(legalField.translationKey)}
		</LinkWithIcon>
	));

	return (
		<Fragment>
			{legalSection}

			<Flex align='start'>
				<ConsentTrigger />
			</Flex>
		</Fragment>
	);
}

function AppSection({ appConfig }: { appConfig: AppConfig | null }): JSX.Element | null {
	const analyticService = useService(AnalyticService);
	const analyticContext = useAnalyticContext();

	function analyticQuirk_handleAppLinkClick(appType: string): (event: MouseEvent<HTMLAnchorElement>) => void {
		return (_) => {
			analyticService.trackEvent(analyticContext, appType === 'IOS' ? 'Clicked: App Store badge' : 'Clicked: Play Store badge');
		};
	}

	if (appConfig?.androidPackageId == null || appConfig?.appStoreId == null) return null;

	return (
		<Stack gap={tokens.spacing.x1}>
			<Text variant='bodySemiBold' color={tokens.color.content.brand}>
				{withTranslation('PW.Component.Footer.DownloadApp.Title')}
			</Text>
			<Flex gap={tokens.spacing.x0_5} direction='row'>
				{appConfig?.androidPackageId && appConfig?.androidDownloadLink ? (
					<elements.appLink
						role='link'
						aria-label={withTranslation('PW.Component.Footer.DownloadApp.label.Android')}
						href={appConfig.androidDownloadLink}
						target='_blank'
						onClick={analyticQuirk_handleAppLinkClick('Android')}
					>
						<elements.appLogoImage
							src={(window.__TEMP__ ? '/PublicV3' : '') + ASSET_IMAGE_PATH + appConfig.androidImageUrl}
							alt='Google play store logo'
						/>
					</elements.appLink>
				) : null}
				{appConfig?.appStoreId && appConfig?.iosDownloadLink ? (
					<elements.appLink
						role='link'
						href={appConfig.iosDownloadLink}
						aria-label={withTranslation('PW.Component.Footer.DownloadApp.label.IOS')}
						target='_blank'
						onClick={analyticQuirk_handleAppLinkClick('IOS')}
					>
						<elements.appLogoImage
							src={(window.__TEMP__ ? '/PublicV3' : '') + ASSET_IMAGE_PATH + appConfig.iosImageUrl}
							alt='App store logo'
						/>
					</elements.appLink>
				) : null}
			</Flex>
		</Stack>
	);
}

// TODO: Use TextLink once available
interface LinkWithIconProps {
	icon: keyof IconDictionary;
	link: string;
	children?: string;
	onClick?(event: MouseEvent<HTMLAnchorElement>): void;
	target?: string;
}

export function LinkWithIcon({ icon, link, children, onClick, target }: LinkWithIconProps): JSX.Element {
	return (
		<elements.link href={link.includes('@') ? `mailto:${link}` : link} target={target ?? '__blank'} onClick={onClick}>
			<Flex direction='row' align='center' gap={tokens.spacing.x0_5}>
				<elements.linkIconContainer>
					<Icon type={icon} size='small' />
				</elements.linkIconContainer>
				<Spacer horizontal={tokens.spacing.x0_5} />
				<Text variant='bodyRegular' color={tokens.color.content.interactive}>
					{children ?? link}
				</Text>
			</Flex>
		</elements.link>
	);
}
